import { useTitrePage } from '../../hooks/useTitrePage';
/**
 * PhilosophyPage - Page présentant la philosophie de l'entreprise
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

export default function PhilosophyPage() {
  useTitrePage('Notre histoire');
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const ecommerceConfig = tenant?.config?.ecommerce;
  const primaryColor = ecommerceConfig?.primaryColor || '#16a34a';

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header simple */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/shop')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Notre Philosophie</h1>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Citation principale */}
        <div className="text-center mb-16">
          <blockquote
            className="text-3xl md:text-4xl font-light italic"
            style={{ color: primaryColor }}
          >
            "Bio dans nos champs, bio dans nos choix. Jamais autrement."
          </blockquote>
        </div>

        {/* Notre histoire */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="text-3xl">🌱</span>
            Notre histoire
          </h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Maraîchers bio depuis 2009, installés à Elouges dans le Hainaut. Philippe, gradué en agronomie
              avec une sensibilité environnementale forgée chez Natagora, en parc naturel et en bureau d'études
              en environnement, a choisi avec Sandrine de mettre les mains dans la terre — pour de bon.
              En 2025, leur fils Paul a rejoint l'aventure : Les Folies Maraîchères, c'est désormais une histoire de famille.
            </p>
            <p className="mt-4">
              Aujourd'hui, nous cultivons plus de <strong>80 variétés de légumes</strong> sous mention
              <strong> Nature et Progrès</strong> : un cahier des charges plus exigeant que le bio européen,
              qui place le respect du vivant au cœur de chaque pratique.
            </p>
          </div>
        </section>

        {/* Notre engagement */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="text-3xl">🤝</span>
            Notre engagement
          </h2>
          <blockquote
            className="border-l-4 pl-6 py-4 my-6 bg-green-50 rounded-r-lg italic text-lg text-gray-700"
            style={{ borderColor: primaryColor }}
          >
            « La bio est pour nous un contrat passé avec notre certificateur, mais avant tout avec nos clients,
            afin qu'ils aient la garantie que le produit qu'ils achètent correspond à leurs attentes. »
          </blockquote>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Depuis 2021, notre magasin à la ferme vous propose notre production et une sélection rigoureuse
              de produits — tous issus de l'agriculture biologique. Pas de compromis, pas de mixte.
              <strong> Chez nous, tout est bio.</strong>
            </p>
          </div>
        </section>

        {/* Nature et Progrès */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="text-3xl">🌿</span>
            Pourquoi Nature et Progrès ?
          </h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              La bio, c'est d'abord une question de bon sens : des aliments cultivés sans produits chimiques
              de synthèse, pour préserver notre santé et celle de la terre.
            </p>
            <p className="mt-4">
              <strong>Nature et Progrès</strong>, c'est plus qu'un label. C'est un mouvement de producteurs
              et de consommateurs qui défend la bio authentique : des sols vivants, une biodiversité préservée,
              une agriculture à taille humaine.
            </p>
          </div>
          {/* Deux choses distinctes, et il ne faut pas les confondre : la
              certification bio est délivrée par un organisme agréé (Certisys),
              Nature et Progrès est une mention portée par une charte associative. */}
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="p-6 bg-white border-2 border-green-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <div className="text-5xl">🏅</div>
                <div>
                  <h3 className="font-bold text-gray-900">Mention Nature et Progrès</h3>
                  <p className="text-gray-600">Un cahier des charges plus exigeant que le bio européen</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-white border-2 border-green-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <div className="text-5xl">✅</div>
                <div>
                  <h3 className="font-bold text-gray-900">Certification biologique : Certisys</h3>
                  <p className="text-gray-600">Organisme de contrôle BE-BIO-01 — Règlement (UE) 2018/848</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Boutique en ligne */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="text-3xl">💻</span>
            Notre boutique en ligne
          </h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Vous nous connaissez au marché, au magasin. Vous appréciez l'échange, le conseil, la confiance.
              Nous voulions retrouver cette proximité en ligne — c'est chose faite.
            </p>
            <p className="mt-4">
              Alors, fidèles à notre philosophie du "fait maison", nous avons développé notre propre plateforme.
              Une boutique où la disponibilité est vérifiée avant que vous ne payiez, un suivi de commande clair,
              un fil d'actualités pour suivre la vie de la ferme et nos arrivages, et
              <strong> la possibilité de commander votre pain à l'avance</strong>, cuit pour vous par notre
              boulanger. Vous commandez quand cela vous arrange, nous vous prévenons dès que c'est prêt.
            </p>
            <p className="mt-4 font-medium" style={{ color: primaryColor }}>
              Le numérique au service de l'humain, pas l'inverse.
            </p>
          </div>

          {/* Features highlight */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="text-2xl mb-2">📦</div>
              <h4 className="font-semibold text-gray-900">Stock en temps réel</h4>
              <p className="text-sm text-gray-600">Ce que vous voyez est réellement disponible</p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="text-2xl mb-2">📢</div>
              <h4 className="font-semibold text-gray-900">Fil d'actualités</h4>
              <p className="text-sm text-gray-600">Suivez la vie de la ferme et nos arrivages</p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="text-2xl mb-2">🥖</div>
              <h4 className="font-semibold text-gray-900">Pain sur commande</h4>
              <p className="text-sm text-gray-600">Cuit pour vous, à retirer le jour choisi</p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="text-2xl mb-2">🏪</div>
              <h4 className="font-semibold text-gray-900">Retrait au magasin</h4>
              <p className="text-sm text-gray-600">Vous êtes prévenu dès que votre commande est prête</p>
            </div>
          </div>
        </section>

        {/* Citation finale */}
        <div className="text-center mt-16 mb-8">
          <blockquote
            className="text-2xl md:text-3xl font-light italic"
            style={{ color: primaryColor }}
          >
            "La bio, c'est tout ce qu'on fait. La cultiver, la choisir, la partager."
          </blockquote>
        </div>

        {/* Bouton retour */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/shop')}
            className="px-8 py-3 text-white font-semibold rounded-lg transition-opacity hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Découvrir notre boutique
          </button>
        </div>
      </main>

    </div>
  );
}
